import apiClient from './apiClient';
import { API_BASE_URL } from '@config/api';

// Folosim API_BASE_URL pentru upload-urile cu fetch (FormData)
const API_URL = API_BASE_URL;

// Tipuri TypeScript pentru Shelters
export interface Shelter {
  id: string;
  name: string;
  cui?: string;
  description?: string;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    county?: string;
    zipCode?: string;
    country?: string;
  };
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  city?: string;
  county?: string;
  working_hours?: any;
  website?: string;
  capacity?: number;
  current_occupancy?: number;
  status: 'invited' | 'active' | 'suspended' | 'inactive';
  owner_user_id?: string;
  created_at: string;
  updated_at: string;
}

export interface ShelterAnimal {
  id: string;
  shelter_id: string;
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other'; // Standardizat
  breed?: string;
  age_months?: number;
  gender: 'male' | 'female'; // Standardizat (renumit din sex)
  size?: 'mica' | 'medie' | 'mare';
  color?: string;
  weight_kg?: number;
  height_cm?: number;
  sterilized: boolean;
  vaccinated: boolean;
  microchipped: boolean;
  chip_number?: string;
  passport_number?: string;
  dewormed: boolean;
  deparasit_extern: boolean;
  vaccinated_rabies: boolean;
  special_needs?: string;
  description?: string;
  status: 'available' | 'reserved' | 'adopted'; // Standardizat
  adoptable: boolean;
  intake_date?: string;
  adoption_date?: string;
  adopted_by_user_id?: string;
  created_at: string;
  updated_at: string;
  photos?: AnimalPhoto[];
  // Pentru compatibilitate cu API-uri vechi
  sex?: 'M' | 'F'; // Deprecated - folosește gender
}

export interface AnimalPhoto {
  id: string;
  animal_id: string;
  file_path: string;
  thumbnail_path?: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  is_cover: boolean;
  sort_order: number;
  uploaded_at: string;
  caption?: string;
}

export interface ShelterInvite {
  id: string;
  shelter_id: string;
  email: string;
  token: string;
  expires_at: string;
  used_at?: string;
  created_at: string;
  shelter_name?: string;
  shelter_status?: string;
}

export interface ImportJob {
  id: string;
  shelter_id: string;
  created_by: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  source_file_url: string;
  image_source: 'url' | 'zip';
  report_url?: string;
  total_rows: number;
  success_count: number;
  error_count: number;
  started_at?: string;
  finished_at?: string;
  created_at: string;
}

// ============= MASTER ADMIN API =============

export const getShelters = async (params?: {
  status?: string;
  city?: string;
  county?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}) => {
  const response = await apiClient.get('/admin/shelters', { params });
  return response.data;
};

export const getShelterById = async (id: string) => {
  const response = await apiClient.get(`/admin/shelters/${id}`);
  return response.data;
};

export const inviteShelter = async (data: {
  email: string;
  cui?: string;
  nume: string;
  oras?: string;
  judet?: string;
}) => {
  const response = await apiClient.post('/admin/shelters/invite', data);
  return response.data;
};

export const getInvites = async (params?: {
  status?: 'pending' | 'used' | 'expired';
  page?: number;
  pageSize?: number;
}) => {
  const response = await apiClient.get('/admin/shelters/invites', { params });
  return response.data;
};

export const resendInvite = async (inviteId: string) => {
  const response = await apiClient.post(`/admin/shelters/invites/${inviteId}/resend`);
  return response.data;
};

export const cancelInvite = async (inviteId: string) => {
  const response = await apiClient.post(`/admin/shelters/invites/${inviteId}/cancel`);
  return response.data;
};

export const updateShelterStatus = async (shelterId: string, status: string) => {
  const response = await apiClient.patch(`/admin/shelters/${shelterId}/status`, { status });
  return response.data;
};

export const impersonateShelter = async (shelterId: string) => {
  const response = await apiClient.post(`/admin/shelters/${shelterId}/impersonate`);
  return response.data;
};

export const stopImpersonation = async () => {
  const response = await apiClient.post('/auth/stop-impersonation');
  return response.data;
};

// ============= SHELTER DASHBOARD API =============

export const getShelterProfile = async () => {
  const response = await apiClient.get('/shelter/profile');
  return response.data;
};

export const updateShelterProfile = async (data: Partial<Shelter>) => {
  const response = await apiClient.patch('/shelter/profile', data);
  return response.data;
};

// ============= ADOPTION APPLICATIONS API =============

export interface AdoptionApplication {
  id: string;
  applicant: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
  };
  animal: {
    id: string;
    name: string;
    species: string;
    breed: string;
    photo?: string;
  };
  status: 'pending' | 'scheduled' | 'approved' | 'rejected';
  preScreeningScore: number;
  notes: string;
  attachments: string[];
  timeline: ApplicationTimeline[];
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationTimeline {
  id: string;
  status: string;
  note: string;
  createdAt: string;
  createdBy: string;
}

export const getAdoptionApplications = async (params?: {
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const response = await apiClient.get('/shelter/applications', { params });
  return response.data;
};

export const getAdoptionApplication = async (id: string) => {
  const response = await apiClient.get(`/shelter/applications/${id}`);
  return response.data;
};

export const scheduleVisit = async (id: string, data: {
  scheduledDate: string;
  notes?: string;
}) => {
  const response = await apiClient.post(`/shelter/applications/${id}/schedule`, data);
  return response.data;
};

export const approveApplication = async (id: string, data: {
  notes?: string;
}) => {
  const response = await apiClient.post(`/shelter/applications/${id}/approve`, data);
  return response.data;
};

export const rejectApplication = async (id: string, data: {
  notes?: string;
}) => {
  const response = await apiClient.post(`/shelter/applications/${id}/reject`, data);
  return response.data;
};

// ============= CALENDAR API =============

export interface CalendarEvent {
  id: string;
  title: string;
  type: 'visit' | 'interview' | 'handover' | 'vaccination' | 'sterilization' | 'other';
  start: string;
  end: string;
  description?: string;
  applicant?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  animal?: {
    id: string;
    name: string;
    species: string;
  };
  status: 'scheduled' | 'completed' | 'cancelled';
  createdBy: string;
  createdAt: string;
}

export const getCalendarEvents = async (params?: {
  startDate?: string;
  endDate?: string;
  type?: string;
}) => {
  const response = await apiClient.get('/shelter/calendar/events', { params });
  return response.data;
};

export const getCalendarEvent = async (id: string) => {
  const response = await apiClient.get(`/shelter/calendar/events/${id}`);
  return response.data;
};

export const createCalendarEvent = async (data: {
  title: string;
  type: string;
  start: string;
  end: string;
  description?: string;
  applicationId?: string;
  animalId?: string;
}) => {
  const response = await apiClient.post('/shelter/calendar/events', data);
  return response.data;
};

export const updateCalendarEvent = async (id: string, data: {
  title?: string;
  type?: string;
  start?: string;
  end?: string;
  description?: string;
  status?: string;
}) => {
  const response = await apiClient.patch(`/shelter/calendar/events/${id}`, data);
  return response.data;
};

export const deleteCalendarEvent = async (id: string) => {
  const response = await apiClient.delete(`/shelter/calendar/events/${id}`);
  return response.data;
};

export const getShelterPhotos = async () => {
  const response = await apiClient.get('/shelter/photos');
  return response.data;
};

export const addShelterPhoto = async (url: string, caption?: string) => {
  const response = await apiClient.post('/shelter/photos', { url, caption });
  return response.data;
};

export const updateShelterPhoto = async (photoId: string, data: { caption?: string; sort_order?: number }) => {
  const response = await apiClient.patch(`/shelter/photos/${photoId}`, data);
  return response.data;
};

export const deleteShelterPhoto = async (photoId: string) => {
  const response = await apiClient.delete(`/shelter/photos/${photoId}`);
  return response.data;
};

// Animals
export const getShelterAnimals = async (params?: {
  species?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}) => {
  const response = await apiClient.get('/shelter/animals', { params });
  return response.data; // API returnează { success: true, animals: [...], total: ... }
};

export const getAnimalById = async (id: string) => {
  const response = await apiClient.get(`/shelter/animals/${id}`);
  return response.data;
};

export const createAnimal = async (data: Partial<ShelterAnimal>) => {
  const response = await apiClient.post('/shelter/animals', data);
  return response.data;
};

export const updateAnimal = async (id: string, data: Partial<ShelterAnimal>) => {
  const response = await apiClient.patch(`/shelter/animals/${id}`, data);
  return response.data;
};

export const deleteAnimal = async (id: string) => {
  const response = await apiClient.delete(`/shelter/animals/${id}`);
  return response.data;
};

export const addAnimalPhoto = async (animalId: string, url: string, caption?: string) => {
  const response = await apiClient.post(`/shelter/animals/${animalId}/photos`, { url, caption });
  return response.data;
};

// Staff
export const getShelterStaff = async () => {
  const response = await apiClient.get('/shelter/staff');
  return response.data;
};

export const inviteStaff = async (email: string, role: 'shelter_admin' | 'shelter_staff') => {
  const response = await apiClient.post('/shelter/staff/invite', { email, role });
  return response.data;
};

// Import
export const createImportJob = async (sourceFileUrl: string, imageSource: 'url' | 'zip' = 'url') => {
  const response = await apiClient.post('/shelter/import', {
    source_file_url: sourceFileUrl,
    image_source: imageSource
  });
  return response.data;
};

export const getImportJobs = async (page = 1, pageSize = 20) => {
  const response = await apiClient.get('/shelter/import-jobs', {
    params: { page, pageSize }
  });
  return response.data.data; // API returnează { success: true, data: jobs, pagination: {...} }
};

export const getImportJob = async (jobId: string) => {
  const response = await apiClient.get(`/shelter/import-jobs/${jobId}`);
  return response.data;
};

export const downloadImportReport = async (jobId: string) => {
  const response = await apiClient.get(`/shelter/import-jobs/${jobId}/report`, {
    responseType: 'blob'
  });
  return response.data;
};

export const downloadImportTemplate = async () => {
  const response = await apiClient.get('/shelter/import-template.csv', {
    responseType: 'blob'
  });
  return response.data;
};

// Activare shelter
export const activateShelter = async (data: {
  token: string;
  parola: string;
  firstName: string;
  lastName: string;
  contactTelefon?: string;
  acceptTermeni: boolean;
}) => {
  const response = await apiClient.post('/auth/shelter/activate', data);
  return response.data;
};

// ============= SHELTER SERVICE CLASS =============

class ShelterService {
  // Dashboard
  async getDashboardStats(_shelterId: string) {
    const response = await apiClient.get(`/shelter/dashboard`);
    return response.data.data;
  }

  // Animals
  async getShelterAnimals(shelterId: string, params?: {
    species?: string;
    status?: string;
    search?: string;
    ageRange?: string;
    sex?: string;
    size?: string;
    compatibility?: string;
    microchipped?: string;
    intakeDate?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await apiClient.get('/shelter/animals', { params });
    return response.data; // API returnează { success: true, animals: [...], total: ... }
  }

  async getAnimalById(id: string) {
    return getAnimalById(id);
  }

  async createAnimal(data: Partial<ShelterAnimal>) {
    return createAnimal(data);
  }

  async createShelterAnimal(data: any) {
    const response = await apiClient.post('/shelter/animals', data);
    return response.data.data; // Returnează obiectul animalului din response.data.data
  }

  async updateAnimal(id: string, data: Partial<ShelterAnimal>) {
    return updateAnimal(id, data);
  }

  async deleteShelterAnimal(id: string) {
    return deleteAnimal(id);
  }

  async uploadAnimalPhoto(file: File, type: 'animal' = 'animal') {
    // Creează FormData complet nou
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    // Folosește fetch API direct - cel mai sigur pentru FormData
    const response = await fetch(`${API_URL}/shelter/animals/photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        // Nu seta Content-Type - lasa browser-ul sa-l seteze automat pentru FormData
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async uploadAnimalPhotos(animalId: string, files: File[]) {
    // Creează FormData pentru poze multiple
    const formData = new FormData();
    files.forEach(file => {
      formData.append('photos', file);
    });

    // Folosește fetch API direct pentru FormData
    const response = await fetch(`${API_URL}/shelter/animals/${animalId}/photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        // Nu seta Content-Type - lasa browser-ul sa-l seteze automat pentru FormData
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async setCoverPhoto(animalId: string, photoId: string) {
    const response = await apiClient.patch(`/shelter/animals/${animalId}/cover-photo`, {
      photoId
    });
    return response.data;
  }

  // Staff
  async getShelterStaff(_shelterId: string) {
    const response = await apiClient.get('/shelter/staff');
    return response.data.data; // API returnează { success: true, data: staff }
  }

  async inviteShelterStaff(_shelterId: string, data: {
    email: string;
    firstName: string;
    lastName: string;
    role: 'shelter_admin' | 'shelter_staff';
  }) {
    return inviteStaff(data.email, data.role);
  }

  async removeShelterStaff(staffId: string) {
    const response = await apiClient.delete(`/shelter/staff/${staffId}`);
    return response.data;
  }

  // Import
  async startImport(shelterId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/shelter/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getImportJobs(_shelterId: string) {
    const response = await apiClient.get('/shelter/import-jobs');
    return response.data.data; // API returnează { success: true, data: jobs, pagination: {...} }
  }

  async downloadImportTemplate() {
    return downloadImportTemplate();
  }

  // Profile
  async getShelterProfile(_shelterId: string) {
    return getShelterProfile();
  }

  async updateShelterProfile(_shelterId: string, data: Partial<Shelter>) {
    return updateShelterProfile(data);
  }
}

export const shelterService = new ShelterService();

